import { useLocalObservable } from 'mobx-react-lite';
import { ElementType, useEffect } from 'react';

import { Call } from '@gem/call/CallStore/Call';
import { MemberDevice } from '@gem/call/CallStore/Member/MemberDevice';
import { useCallContext } from '@gem/call/CallView/CallView.context';
import { MemberListDisplayMode } from '@gem/call/Model/callModel.types';
import { IcRemoveUserIcon, IcMicOff24Icon } from '@gem/icons';

interface IMemberCardActions {
  key: string;
  icon: ElementType;
  onClick(): void;
}

interface IMemberCardPresenter {
  call: Call;
  memberDevice: MemberDevice;
  muteMember(): void;
  actions: IMemberCardActions[];
  removeMember(): void;
  isShowSpeakerRole: boolean;
}

export const useMemberCardPresenter = (memberDevice: MemberDevice): IMemberCardPresenter => {
  const context = useCallContext();

  const presenter = useLocalObservable<IMemberCardPresenter>(() => ({
    memberDevice: memberDevice,

    get call(): Call {
      return context.call;
    },

    muteMember(): void {
      if (!presenter.memberDevice.deviceId) {
        return;
      }

      context.callController.muteMember(
        presenter.memberDevice.memberId,
        presenter.memberDevice.deviceId
      );
    },

    removeMember(): void {
      if (!presenter.memberDevice.deviceId) {
        return;
      }
      context.callController.removeMemberFromCall(
        presenter.memberDevice.memberId,
        presenter.memberDevice.deviceId
      );
    },

    get actions(): IMemberCardActions[] {
      const actions = [];
      const call = presenter.call;

      if (call.isAvailableMuteAudio && presenter.memberDevice.isMuting) {
        actions.push({
          key: 'mute',
          icon: IcMicOff24Icon,
          onClick: presenter.muteMember,
        });
      }

      if (call.isAvailableRemoveMember && presenter.memberDevice.isRemoving) {
        actions.push({
          key: 'remove',
          icon: IcRemoveUserIcon,
          onClick: presenter.removeMember,
        });
      }

      return actions;
    },

    get isShowSpeakerRole(): boolean {
      return presenter.call.uiFeatures.memberListDisplayMode === MemberListDisplayMode.NameWithRole;
    },
  }));

  useEffect(() => {
    presenter.memberDevice = memberDevice;
  }, [memberDevice]);

  return presenter;
};
